import "./src/styles/global.css";
import "./src/styles/v2.css";
import { RootWrapper } from "./src/root-wrapper";
import { captureInstallPrompt } from "./src/lib/installPrompt";

export const wrapRootElement = RootWrapper;

/**
 * Where a route change leaves the scroll position.
 *
 * <h4>The bug this fixes</h4>
 * Arriving at a screen part-way down it. Every view inside {@code /app} already scrolls itself to
 * the top on a route change — {@code AppShell} has done that all along — and it was being undone
 * afterwards by Gatsby's own scroll restoration.
 *
 * <h4>Why returning [0, 0] would not have fixed it</h4>
 * The documented return value is a coordinate pair, but the handler that consumes it
 * (`gatsby-react-router-scroll/scroll-handler.js`) uses this only as a boolean gate:
 *
 *   windowScroll(position, prevProps) {
 *     if (this.shouldUpdateScroll(prevProps, this.props)) window.scrollTo(0, position)
 *   }
 *
 * `position` comes from session storage keyed on `location.key`, never from what this returns — so
 * `[0, 0]` is merely truthy and scrolls to the stored offset, which is the very value that is
 * wrong. (Gatsby's own source calls the arrangement "a big code smell" in a FIXME.) The only
 * answer the gate understands is `false`: do not scroll at all, and leave it to the shell.
 *
 * <h4>Scoped to /app, and why</h4>
 * `false` means *nothing* touches the scroll, which is only safe where something else already
 * does. That is true inside the app shell and not true of the public pages — on a phone the login
 * form scrolls, and returning `false` there would carry a reader's position from half-way down it
 * straight into /forgot-password. Those keep Gatsby's default.
 */
export const shouldUpdateScroll = ({ routerProps }) => {
  const pathname = routerProps?.location?.pathname || "";
  return !pathname.startsWith("/app");
};

/**
 * Take a new version the moment it is ready, instead of two page loads later.
 *
 * gatsby-plugin-offline's default is to install the new service worker in the background and
 * leave the old one serving the page until every tab has closed. For a site that is mostly read
 * that's polite. Here it means a rebuild appears to have done nothing — you refresh, get the
 * previous bundle, and start debugging code that isn't running. Worse, the mix is per-asset, so
 * you can end up with a new page shell calling into stale chunks, which fails in ways that look
 * like real bugs rather than a cache.
 *
 * Reloading here is safe because this app keeps nothing unsaved in the page: forms submit
 * straight to the API, and auth lives in cookies rather than memory.
 */
export const onServiceWorkerUpdateReady = () => {
  window.location.reload();
};

/**
 * In development, evict any service worker found on this origin.
 *
 * gatsby-plugin-offline only registers a worker in production builds, so a worker present during
 * `gatsby develop` is stale by definition — left behind by a `gatsby serve` on the same port. It
 * then answers the dev server's requests with precached production HTML, and the dev loader dies
 * on `window.___chunkMapping` being undefined, because the document it got was built for a
 * different bundle entirely. The error points at .cache/loader.js and looks nothing like its
 * cause, which is what makes it worth handling here rather than leaving people to find it.
 *
 * One reload, not a loop: the worker is gone by then, so the next pass finds nothing to do.
 */
export const onClientEntry = () => {
  // The browser offers installing once, early: kept for the dashboard's install card (lib/installPrompt).
  captureInstallPrompt();
  if (process.env.NODE_ENV !== "development") return;
  if (typeof navigator === "undefined" || !navigator.serviceWorker) return;

  navigator.serviceWorker.getRegistrations().then((regs) => {
    if (!regs.length) return;
    // eslint-disable-next-line no-console
    console.warn("[dev] Removing a stale service worker left by a production build, and reloading.");
    Promise.all(regs.map((r) => r.unregister()))
      .then(() => (typeof caches === "undefined" ? [] : caches.keys()))
      .then((keys) => Promise.all(keys.map((k) => caches.delete(k))))
      .then(() => window.location.reload())
      .catch(() => { /* nothing else to try; a manual hard refresh still works */ });
  }).catch(() => { /* browser refused to enumerate — nothing to clean up */ });
};
